import React from 'react';
import { Tooltip } from 'antd';
import { Link } from 'react-router-dom';

import { BLANK_ICON, CARD_ICON, GRID, LOCK, PUZZLE, TAG_ICON } from '../icons';
import LibraryIcon from './LibraryIcon';
import { isLibraryIconName } from '../../utils/libraryIcons';
import { identityGlyphColor, toRgba } from '../../utils/colors';
import useCollectionIdentity, { useQualifier } from '../../hooks/useCollectionIdentity';
import {
	qualifiedAccessibleName,
	qualifierNote,
	qualifierSpokenText,
} from '../../utils/recordQualifier';
import { EXTERNAL_LINK_PROPS } from './ExternalLink';

/**
 * The record badge: keepr's smallest unit of identity.
 *
 * Every reference to a collection, a card definition, or an item — a list
 * row, a table cell, a picker option, a modal lead, a page title — renders
 * through here, so the platform reads as one design. A badge is exactly two
 * things inside one bordered pill: the record's icon tile (its icon on its
 * color) and its title — for an ITEM, the title with the card's name as a
 * muted line above it (`kicker`), the tile to the left of both. Nothing
 * else shares that space. Dates, breadcrumbs, secondary text, and actions
 * sit beneath or off to the side of it — see RecordIdentity, which lays
 * those out.
 *
 *   <RecordBadge kind="item" record={card} kicker={card.name} title={itemTitle} />
 *   <RecordBadge kind="collection" record={collection} title={name} to={path} size="xl" titleAs="h1" />
 *   <RecordBadge kind="card" record={card} title={card.name} size="xs" />
 *
 * `record` is anything shaped { icon, color, name } — a collection, a card
 * definition, an element set, or (for an item) the item's card definition,
 * whose identity the item wears. `kind` (collection · card · item ·
 * elementSet · tag) picks the icon fallback and the accessible role
 * description. A TAG is a badge like any record (docs/plans/tags.md §5.1):
 * its icon tile on its own color, then its name — and nothing else inside
 * the pill; its glyphs and ✕ sit outside it (components/tags/TagBadge.js).
 * `to` makes the whole badge a router link; `href` an external
 * link; `onClick` a button. With none of them it is a plain inline element.
 *
 * THE QUALIFIED BADGE. `collection` names the collection that OWNS the record
 * (an id, or `{ _id, name, icon, color }`). When the surface the badge is drawn
 * on is not that collection (contexts/CollectionScopeContext — the rule is
 * utils/recordQualifier.js), the pill grows to the LEFT: the collection's small
 * stacked tile and its name, muted, a thin divider, then the record exactly as
 * it reads unqualified. One pill, one link target (the record); it grows
 * sideways, never taller. On the record's own collection, on a public page,
 * or while the collection cannot be resolved, it renders as it always has.
 * `collectionRelation` says how a row a list shows from elsewhere is related
 * ('link' — the segment then carries the read-only lock —, 'family',
 * 'parent'); it is in the tooltip and in what a screen reader hears.
 *
 * The segment is SEEN first and HEARD last: it is aria-hidden where it stands,
 * and visually hidden words at the end of the pill say "…, in Clinic (linked,
 * read-only)" after the record — on an inert badge too, where an aria-label
 * would be ignored. The name is whole in the DOM; CSS cuts it.
 *
 *   <RecordBadge kind="item" record={card} kicker="Book" title="Dune" collection={item.collection_id} to={path} />
 */

// Tile sizes per token (px). Scaled up 1.5x in Sep 2026 so the identity
// chip reads at a glance; the pill's text grows only a point or two with it.
export const BADGE_TILE_SIZES = { xs: 24, sm: 30, md: 36, lg: 48, xl: 60 };

// The plain variant (a record's OWN surface: chip + plain title, no pill)
// runs a slightly smaller chip per size — the title is not boxed, so the
// chip does not need to carry the pill's height. xs table cells 24, sm
// table rows 27, md index cards 36, lg the detail pane 42, xl the context
// bar 48.
export const PLAIN_TILE_SIZES = { xs: 24, sm: 27, md: 36, lg: 42, xl: 48 };

// The qualifier's stacked collection tile per badge size — small enough that
// the qualified pill is never taller than the badge it qualifies.
export const QUALIFIER_TILE_SIZES = { xs: 18, sm: 20, md: 22, lg: 28, xl: 34 };

const FALLBACK_COLOR = '#94a3b8';

// Collections with no icon get a blank swatch (never a card glyph); cards and
// items fall back to the card glyph; an element set (the bolt-on bundle a
// card attaches, docs/plans/element-sets-and-me.md) to the puzzle piece; a
// tag (docs/plans/tags.md §5.1) to the tag glyph.
// A dashboard (charts A7: a launcher on Home) wears the grid glyph.
const KIND_FALLBACK_ICON = { collection: BLANK_ICON, card: CARD_ICON, item: CARD_ICON, elementSet: PUZZLE, tag: TAG_ICON, dashboard: GRID };

const KIND_ROLE = { collection: 'Collection', card: 'Card definition', item: 'Item', elementSet: 'Element set', tag: 'Tag', dashboard: 'Dashboard' };

// The kind word printed above the title where the pill has the height for it.
const KIND_LABEL = { collection: 'collection', card: 'card', item: 'item' };

// A two-line text block (label + title) is about 32px tall at md; below a
// 36px tile it would make the one-line row taller than its tile, which is
// what a table cell, a list row or a rail pin cannot afford. Those rows keep
// the glyph's shape (stack vs single tile) as the only kind signal.
export const KIND_LABEL_MIN_TILE = 36;

const resolvePx = (size, sizes = BADGE_TILE_SIZES) => {
	if (typeof size === 'number' && Number.isFinite(size)) return size;
	return sizes[size] || sizes.md;
};

const resolveSizeToken = (size) => (
	typeof size === 'string' && BADGE_TILE_SIZES[size] ? size : 'md'
);

/**
 * The icon tile alone — the record's icon on its color. Holds its shape and
 * size whether or not the record has an icon or color set, so rows never
 * shift, and the icon color follows the background's contrast so any
 * user-picked color stays legible. Use it standalone only where the title
 * is spoken elsewhere on the same row (a collapsed nav rail, an avatar-style
 * stack); everywhere else render the full badge.
 *
 * THE SHAPE SAYS THE KIND. A COLLECTION's tile is a stack — a second layer
 * peeks out behind it, up and to the right, in the collection's own colour at
 * reduced strength: a container holding things. A card's tile (and an item's,
 * which wears its card's) is a single tile. People are round (UserChip). The
 * layer is drawn inside the tile's own footprint (identity.css
 * `.record-badge__tile--collection`), so a collection tile takes exactly the
 * room a card tile does, at every size.
 */
export function RecordIconTile({
	record,
	kind = 'card',
	size = 'md',
	fallbackIcon,
	className = '',
	title,
}) {
	const px = resolvePx(size);
	const background = record?.color || FALLBACK_COLOR;
	const Fallback = fallbackIcon === undefined ? KIND_FALLBACK_ICON[kind] || CARD_ICON : fallbackIcon;
	const iconName = typeof record?.icon === 'string' ? record.icon : '';
	// The record's own icon is a library icon, drawn on demand (LibraryIcon,
	// KPR-1187): the tile is its final size at once, the glyph appears when its
	// file arrives. A name the library does not have, or a file that fails,
	// draws the kind's fallback glyph — or, where the caller asked for none, the
	// initial.
	let glyph;
	if (iconName && (Fallback || isLibraryIconName(iconName))) glyph = <LibraryIcon name={iconName} fallback={Fallback || null} />;
	else if (Fallback) glyph = <Fallback />;
	else glyph = <span className="record-badge__tile-initial">{(record?.name || '?').slice(0, 1)}</span>;
	const stacked = kind === 'collection';
	const classes = ['record-badge__tile', stacked ? 'record-badge__tile--collection' : '', className].filter(Boolean).join(' ');

	return (
		<span
			className={classes}
			style={{
				'--tile-size': `${px}px`,
				// The stacked layer's colour: the collection's own, which the
				// stylesheet mixes down to reduced strength.
				...(stacked ? { '--tile-color': background } : null),
				backgroundColor: background,
				color: identityGlyphColor(background),
			}}
			title={title}
			aria-hidden="true"
		>
			{glyph}
		</span>
	);
}

/**
 * Several records' tiles, overlapping — the identity of something that IS
 * several records (a multi add form: the cards its steps create). Up to
 * `max` tiles, the rest folded into a "+N" tile so the stack never grows
 * past four. Sizes as RecordIconTile. Renders a single tile unchanged when
 * there is one record, and a blank tile when there are none, so a badge
 * built on it keeps its shape.
 */
export function RecordTileStack({ records = [], kind = 'card', size = 'md', max = 3, className = '' }) {
	const list = (Array.isArray(records) ? records : []).filter(Boolean);
	const px = resolvePx(size);
	const shown = list.length > max ? list.slice(0, max) : list;
	const more = list.length - shown.length;
	const classes = ['record-tile-stack', className].filter(Boolean).join(' ');
	if (!list.length) return <RecordIconTile record={null} kind={kind} size={size} className={className} />;
	return (
		<span className={classes} style={{ '--tile-size': `${px}px` }} aria-hidden="true">
			{shown.map((record, index) => (
				<RecordIconTile
					key={`${record?._id || record?.name || 'r'}-${index}`}
					record={record}
					kind={kind}
					size={size}
					className="record-tile-stack__tile"
				/>
			))}
			{more > 0 ? (
				<span className="record-badge__tile record-tile-stack__tile record-tile-stack__more" style={{ '--tile-size': `${px}px` }}>
					<span className="record-badge__tile-initial">+{more}</span>
				</span>
			) : null}
		</span>
	);
}

/**
 * A record's own title: chip + plain text, no pill. Sugar for
 * `<RecordBadge variant="plain" />` — use it wherever the record IS the
 * surface (its context bar, its row in a list, its own card on an index,
 * the detail pane head). The bordered badge is for references made from
 * somewhere else.
 */
export function RecordTitle(props) {
	return <RecordBadge variant="plain" {...props} />;
}

/**
 * The collection segment of a qualified badge — the collection's small
 * stacked tile and its name, muted (whole in the DOM, cut by CSS past about
 * thirteen characters, and dropped altogether when fewer than four would
 * show), with the whole name and the relation in its tooltip (`note`, else
 * "In Biewend" / the relation's sentence — a function is handed the resolved
 * identity). A linked row's segment (`relation="link"`) carries the lock:
 * read-only here. RecordBadge draws it at the pill's left edge; standing
 * alone it is the "which collection" mark beside a label that is not a badge
 * (a filter field read through a lookup, a launcher that makes its item in
 * another collection), and then says itself in place ("in Clinic") — unless
 * `spoken={false}`, where the caller puts those words after its own label.
 * `divider` draws the thin divider after it, only when it renders.
 * `collection` is an identity (`{ _id, name, icon, color }`, or at least
 * `{ _id, name }` — the resolver fills in the rest), or an id the resolver
 * can name; `resolved` hands over an identity already resolved (RecordBadge
 * does, so what is shown and what is spoken are one answer). Renders nothing
 * until it can say which collection.
 */
export function RecordQualifier({
	collection,
	size = 'sm',
	note = null,
	relation = null,
	className = '',
	spoken = true,
	divider = false,
	resolved,
}) {
	const looked = useCollectionIdentity(resolved === undefined ? collection : null);
	const identity = resolved === undefined ? looked : resolved;
	if (!identity) return null;
	const sizeToken = resolveSizeToken(size);
	const tooltip = typeof note === 'function' ? note(identity) : (note || qualifierNote(identity, relation));
	return (
		<>
			<Tooltip title={tooltip}>
				<span
					className={['record-qualifier', `record-qualifier--${sizeToken}`, className].filter(Boolean).join(' ')}
					data-testid="record-qualifier"
					data-collection-id={identity._id}
					data-relation={relation || undefined}
					// Seen first, heard last: the words are said after the record.
					aria-hidden="true"
					// An empty title stops a badge-wide `tooltip` (a native title on
					// the pill) from doubling up with this segment's own tooltip.
					title=""
				>
					<RecordIconTile record={identity} kind="collection" size={QUALIFIER_TILE_SIZES[sizeToken]} className="record-qualifier__tile" />
					{relation === 'link' ? <LOCK className="record-qualifier__lock" aria-hidden="true" /> : null}
					<span className="record-qualifier__name">{identity.name}</span>
				</span>
			</Tooltip>
			{spoken ? <span className="record-badge__spoken">{qualifierSpokenText(identity, relation)}</span> : null}
			{divider ? <span className="record-badge__divider" aria-hidden="true" /> : null}
		</>
	);
}

export default function RecordBadge({
	record,
	// Several records at once (RecordTileStack) — the badge of a thing that
	// is several records, such as a multi add form. Wins over `record`.
	records,
	kind = 'card',
	title,
	// The muted line ABOVE the title, inside the badge: an item's card name.
	// With it the badge reads as two lines beside the tile; without it, one.
	kicker,
	size = 'md',
	// 'badge' (default): the bordered pill — a reference to the record from
	// elsewhere. 'plain': chip + plain title — the record's own surface.
	variant = 'badge',
	to,
	href,
	onClick,
	titleAs: TitleTag = 'span',
	className = '',
	block = false,
	wrap = false,
	active = false,
	fallbackIcon,
	tileSize,
	// An explicit hover tooltip. Not derived from `title` automatically: a
	// badge inside an antd option would then duplicate the option's own
	// title attribute, and the text is already visible.
	tooltip,
	// The collection that OWNS the record — an id or `{ _id, name, icon,
	// color }`. Qualifies the badge wherever it is drawn outside that
	// collection (see the header). `collectionRelation` ('link' | 'family' |
	// 'parent') says how a row from elsewhere is related; `collectionNote`
	// overrides the segment's tooltip.
	collection = null,
	collectionRelation = null,
	collectionNote = null,
	// Opt out of the kind label on a surface that already says the kind.
	noKindLabel = false,
	...rest
}) {
	const qualifier = useQualifier(collection);
	const sizeToken = resolveSizeToken(size);
	const plain = variant === 'plain';
	const tilePx = plain ? resolvePx(tileSize || sizeToken, PLAIN_TILE_SIZES) : (tileSize || sizeToken);
	const accent = record?.color || (Array.isArray(records) && records[0]?.color) || FALLBACK_COLOR;
	const hasKicker = kicker !== undefined && kicker !== null && kicker !== '';
	// The kind label stands in for a kicker only when there is none: an item's
	// card name already is its line above the title, and a third line would
	// grow the pill. Not for tags, element sets or multi-record badges.
	const tileForLabel = plain ? resolvePx(tileSize || sizeToken, PLAIN_TILE_SIZES) : resolvePx(tileSize || sizeToken);
	const kindLabel = !hasKicker && !Array.isArray(records) && !noKindLabel && tileForLabel >= KIND_LABEL_MIN_TILE
		? KIND_LABEL[kind] || null
		: null;
	const classes = [
		'record-badge',
		`record-badge--${sizeToken}`,
		plain ? 'record-badge--plain' : '',
		hasKicker || kindLabel ? 'record-badge--two-line' : '',
		qualifier ? 'record-badge--qualified' : '',
		block ? 'record-badge--block' : '',
		wrap ? 'record-badge--wrap' : '',
		active ? 'record-badge--active' : '',
		className,
	].filter(Boolean).join(' ');

	const style = {
		'--badge-accent': accent,
		'--badge-ring': toRgba(accent, 0.32) || undefined,
	};

	const titleAttr = tooltip || undefined;

	const content = (
		<>
			{qualifier ? (
				<RecordQualifier
					resolved={qualifier}
					size={sizeToken}
					note={collectionNote}
					relation={collectionRelation}
					spoken={false}
					divider
					className="record-badge__qualifier"
				/>
			) : null}
			{Array.isArray(records) ? (
				<RecordTileStack records={records} kind={kind} size={tilePx} />
			) : (
				<RecordIconTile
					record={record}
					kind={kind}
					size={tilePx}
					fallbackIcon={fallbackIcon}
				/>
			)}
			{hasKicker ? (
				<span className="record-badge__text">
					<span className="record-badge__kicker">{kicker}</span>
					<TitleTag className="record-badge__title">{title}</TitleTag>
				</span>
			) : kindLabel ? (
				<span className="record-badge__text">
					{/* aria-hidden: the accessible name stays the title alone
					    (a link is named by its content, and a role description
					    already says the kind on an inert badge). */}
					<span className="record-badge__kicker record-badge__kind" aria-hidden="true">{kindLabel}</span>
					<TitleTag className="record-badge__title">{title}</TitleTag>
				</span>
			) : (
				<TitleTag className="record-badge__title">{title}</TitleTag>
			)}
			{/* Where it lives, said after the record — the segment it
			    repeats is aria-hidden where it stands. */}
			{qualifier ? <span className="record-badge__spoken">{`, ${qualifierSpokenText(qualifier, collectionRelation)}`}</span> : null}
		</>
	);

	// A qualified badge is NAMED record first: "Dune, Book, in Biewend". A
	// link or a button takes it as its name; so does a control that holds an
	// inert badge and names itself from its content (a Home launcher). A
	// screen reader reading an inert badge in place may skip a span's label —
	// the hidden words above say the same there. A caller's own `aria-label`
	// still wins.
	const qualifiedLabel = qualifier && typeof title === 'string'
		? qualifiedAccessibleName({ title, kicker: typeof kicker === 'string' ? kicker : null, identity: qualifier, relation: collectionRelation })
		: null;

	const shared = {
		className: classes,
		style,
		title: titleAttr,
		'data-record-kind': kind,
		'data-record-variant': variant,
		...(qualifier ? { 'data-qualified-by': qualifier._id } : null),
		...(qualifiedLabel ? { 'aria-label': qualifiedLabel } : null),
		...rest,
	};

	if (to) {
		return <Link to={to} {...shared} onClick={onClick}>{content}</Link>;
	}
	if (href) {
		return (
			<a href={href} {...EXTERNAL_LINK_PROPS} {...shared} onClick={onClick}>
				{content}
			</a>
		);
	}
	if (typeof onClick === 'function') {
		return <button type="button" {...shared} onClick={onClick}>{content}</button>;
	}
	return <span {...shared} aria-roledescription={KIND_ROLE[kind]}>{content}</span>;
}

/**
 * The identity block: a badge with its context laid out AROUND it, never in
 * it. `kicker` sits above (a micro-label: "Collection", "Card definition", a
 * breadcrumb — an ITEM's card name goes inside its badge instead, see
 * RecordBadge `kicker`); `below` sits beneath (dates, meta, provenance, tags); and
 * `actions` get their own column at the far right. This is the one
 * arrangement used by every panel head, page title, modal lead, and list
 * row that names a record.
 *
 *   <RecordIdentity
 *     kicker="Progress note"
 *     badge={<RecordBadge kind="item" record={card} title={title} size="lg" />}
 *     below={<span>Added Sep 1, 2026</span>}
 *     actions={<Button icon={<KEBAB />} />}
 *   />
 */
export function RecordIdentity({
	kicker = null,
	badge,
	below = null,
	actions = null,
	className = '',
	align = 'start',
}) {
	const classes = [
		'record-identity',
		align === 'center' ? 'record-identity--center' : '',
		className,
	].filter(Boolean).join(' ');

	return (
		<div className={classes}>
			<div className="record-identity__main">
				{kicker ? <div className="record-identity__kicker micro-label">{kicker}</div> : null}
				<div className="record-identity__badge">{badge}</div>
				{below ? <div className="record-identity__below">{below}</div> : null}
			</div>
			{actions ? <div className="record-identity__actions">{actions}</div> : null}
		</div>
	);
}
